import { useState, useCallback, useEffect } from "react";
import "../../../assets/css/reservation.css";
import { useNavigate } from "react-router-dom";
import { ReservationsController } from "../../../controllers/reservationsController";
import { ReservationSlotSettingsController } from "../../../controllers/reservationSlotSettingsController";
import { validateReservationSettingsForm } from "../../../validations/reservationForm";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { Dropdown } from "../../../components/dropdown";
import { useToastPromise } from "../../../components/toastPromise";
import { ApiUtil } from "../../../util/apiUtil";
import { Button } from "react-bootstrap";
import { Loading } from "../../../components/loading";
import { ReservationSettingsController } from "../../../controllers/reservationSettingsController";
import { SlotButtons } from "../../../components/slotButtons";
import { DateUtil } from "../../../util/dateUtil";
import { TableName } from "../../../constants/tableName";
import { SlotStatusList } from "../../../constants/slotStatusList";

export const Settings = ({
  isOpen = false,
  reservationSettingsId,
  reservationType = TableName.NORMALRESERVATIONS,
  backFunction = () => {},
}) => {
  const initialInputs = {
    reservationDate: DateUtil.toYYYYMMDDFormat(new Date()),
    timeSlot: {},
    newSlotStatus: "",
  };
  const toastPromise = useToastPromise();
  const navigate = useNavigate();
  const [user] = useState(() => JSON.parse(localStorage.getItem("user")));
  const [loading, setLoading] = useState(true);
  const [reservationSettings, setReservationSettings] = useState({});
  const [newSlotStatus, setNewSlotStatus] = useState([]);
  const [filteredEventSlots, setFilteredEventSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [inputs, setInputs] = useState(initialInputs);
  const [errors, setErrors] = useState({});
  const [isEventSelection, setIsEventSelection] = useState(false);
  const initialSelectedDate = {
    weekend: null,
    weekday: null,
  };
  const [selectedDate, setSelectedDate] = useState(initialSelectedDate);
  const eventDates = reservationSettings?.eventSettings?.timeSlotsList
    ? reservationSettings?.eventSettings?.timeSlotsList?.map((slot) =>
        new Date(slot.eventDate).toDateString()
      )
    : [];
  const handleOnChange = useCallback((value, name) => {
    setInputs((prev) => ({ ...prev, [name]: value }));
  }, []);
  const handleOnClick = (type) => (event, slot) => {
    setIsEventSelection(type === "event");
    setSelectedSlot(slot.slotTitle);
    setInputs((prev) => ({
      ...prev,
      timeSlot: slot,
      newSlotStatus: slot.slotStatus,
    }));
  };
  const handleDate = (dateTime) => {
    const selectedDateFormatted = DateUtil.toYYYYMMDDFormat(dateTime);
    setInputs((prev) => ({
      ...prev,
      reservationDate: getFilteredEventSlots(
        dateTime,
        reservationSettings?.eventSettings?.timeSlotsList
      ),
    }));
    if (dateTime.getDay() === 0 || dateTime.getDay() === 6) {
      setSelectedDate({ weekend: selectedDateFormatted, weekday: null });
    } else {
      setSelectedDate({ weekday: selectedDateFormatted, weekend: null });
    }
    setSelectedSlot(null);
    setInputs((prev) => ({ ...prev, timeSlot: {}, newSlotStatus: "" }));
  };
  const handleSubmit = (event) => {
    event.preventDefault();
    const validationErrors = validateReservationSettingsForm(inputs);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length) {
      console.debug("Form submission failed due to validation errors.");
      return;
    }
    console.debug("Form validated successfully.");
    saveReservationSlotSettings();
  };
  const resetForm = () => {
    setInputs(initialInputs);
    setErrors({});
    setSelectedSlot(null);
    setSelectedDate(initialSelectedDate);
  };
  const saveReservationSlotSettings = () => {
    setLoading(true);
    toastPromise({
      promise: new Promise((resolve, reject) => {
        ReservationSlotSettingsController.upsert({
          type: isEventSelection
            ? TableName.EVENTRESERVATIONSLOTSETTINGS
            : TableName.NORMALRESERVATIONSLOTSETTINGS,
          parameters: {
            reservationDate: inputs.reservationDate,
            timeSlot: {
              ...inputs.timeSlot,
              newSlotStatus: inputs.newSlotStatus,
            },
            reservationSettingsId: reservationSettings.id,
            createdBy: user.uid,
            createdDate: new Date().toLocaleString(),
            lastModifiedBy: user.uid,
            lastModifiedDate: new Date().toLocaleString(),
          },
          resolve: resolve,
          reject: reject,
        });
      }),
      successMessage: (data) => data,
      errorMessage: (data) => data,
      finishPromise: () => {
        getAll(reservationSettingsId);
        resetForm();
        setLoading(false);
      },
      failPromise: () => {
        setLoading(false);
        !user
          ? navigate("/loginSignUp", { state: { isLoginToggle: true } })
          : resetForm();
      },
    });
  };
  const getFilteredEventSlots = (dateTime, eventTimeSlots = []) => {
    const selectedDateFormatted = DateUtil.toYYYYMMDDFormat(dateTime);
    setFilteredEventSlots(
      eventTimeSlots.filter((data) => data.eventDate === selectedDateFormatted)
    );
    return selectedDateFormatted;
  };
  const getFilteredTodaySlots = async (timeSlots) => {
    return await checkEachSlotStatus(timeSlots);
  };
  const checkEachSlotStatus = (timeSlots) => {
    const getSlotStatus = (timeSlot) =>
      new Promise((resolve, reject) => {
        ReservationSlotSettingsController.getNewSlotStatus({
          type: isEventSelection
            ? TableName.EVENTRESERVATIONSLOTSETTINGS
            : TableName.NORMALRESERVATIONSLOTSETTINGS,
          reservationSettingsId,
          reservationDate: inputs.reservationDate,
          timeSlotsId: timeSlot.id,
          resolve,
          reject,
        });
      }).then((data) =>
        data
          ? { ...timeSlot, slotStatus: data }
          : new Promise((resolve, reject) => {
              ReservationsController.checkEachSlotStatus({
                type: reservationType,
                reservationDate: inputs.reservationDate,
                timeSlotsId: timeSlot.id,
                maxPax: timeSlot.maxPax,
                slotStatus: timeSlot.slotStatus,
                resolve,
                reject,
              });
            }).then((data) => ({ ...timeSlot, slotStatus: data }))
      );
    return Promise.all(timeSlots.map(getSlotStatus));
  };
  const getAllReservationSettings = (reservationSettingsId) => {
    return new Promise((resolve, reject) => {
      ReservationSettingsController.getViaId({
        id: reservationSettingsId,
        resolve: resolve,
        reject: reject,
      });
    });
  };
  const getAll = (reservationSettingsId) => {
    Promise.all([getAllReservationSettings(reservationSettingsId)])
      .then(([reservationSettingsData]) => {
        const settings = ApiUtil.deserialize({
          data: reservationSettingsData,
        });
        setReservationSettings(settings);
        getFilteredEventSlots(
          new Date(),
          settings.eventSettings?.timeSlotsList
        );
      })
      .catch((error) => console.debug(error))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    getAll(reservationSettingsId);
  }, []);
  useEffect(() => {
    if (!user) navigate("/loginSignUp", { state: { isLoginToggle: true } });
  }, [user]);
  useEffect(() => {
    if (isOpen && reservationSettingsId && reservationType) {
      setIsEventSelection(reservationType === TableName.EVENTRESERVATIONS);
      setLoading(false);
    }
  }, [isOpen, reservationSettingsId, reservationType]);
  useEffect(() => {
    const fetchTimeSlots = async () => {
      let slotsToCheck;
      if (isEventSelection) {
        slotsToCheck = filteredEventSlots;
      } else if (
        !isEventSelection &&
        (filteredEventSlots[0]?.eventDate === selectedDate.weekday ||
          filteredEventSlots[0]?.eventDate === selectedDate.weekend)
      ) {
        slotsToCheck = filteredEventSlots[0]?.availableNormalTimeSlots;
      } else {
        slotsToCheck = reservationSettings?.normalSettings?.timeSlotsList;
      }
      if (slotsToCheck) {
        const filteredSlots = await getFilteredTodaySlots(slotsToCheck);
        setNewSlotStatus(filteredSlots);
      }
    };
    fetchTimeSlots();
  }, [selectedDate, filteredEventSlots, reservationSettings]);

  return loading ? (
    <Loading />
  ) : (
    <section id="settings">
      <div className="reservation-container">
        <form onSubmit={handleSubmit} className="margin-bottom">
          <div>
            <h3>SLOTS SETTINGS</h3>
            <hr />
            <br />
            <div className="row-wrapper">
              <div className="calendar-div">
                <Calendar
                  onChange={handleDate}
                  value={inputs.reservationDate}
                  className="custom-calendar margin-bottom"
                  tileClassName={({ date, view }) => {
                    if (view === "month") {
                      const dateFormatted = DateUtil.toYYYYMMDDFormat(date);
                      if (date.toDateString() === new Date().toDateString())
                        return selectedDate.weekday === dateFormatted ||
                          selectedDate.weekend === dateFormatted
                          ? "selected-today"
                          : "today";
                      if (date.getDay() === 0 || date.getDay() === 6) {
                        return selectedDate.weekend === dateFormatted
                          ? "selected-weekend"
                          : isEventSelection &&
                            !eventDates.includes(date.toDateString())
                          ? "weekend unselectable-date"
                          : "weekend";
                      } else {
                        return selectedDate.weekday === dateFormatted
                          ? "selected-weekday"
                          : isEventSelection &&
                            !eventDates.includes(date.toDateString())
                          ? "weekday unselectable-date"
                          : "weekday";
                      }
                    }
                    return "";
                  }}
                />
              </div>
              {
                <div className="detail-div">
                  {isEventSelection ? (
                    <div>
                      <SlotButtons
                        name="timeSlot"
                        title="Event Slots"
                        timeSlots={newSlotStatus}
                        handleOnClick={handleOnClick("event")}
                        selectedSlot={selectedSlot}
                        errorMsg={errors.timeSlot}
                        isManager={true}
                      />
                    </div>
                  ) : (
                    <SlotButtons
                      name="timeSlot"
                      title="Normal Slots"
                      timeSlots={newSlotStatus}
                      handleOnClick={handleOnClick("normal")}
                      selectedSlot={selectedSlot}
                      errorMsg={errors.timeSlot}
                      isManager={true}
                    />
                  )}
                  <Dropdown
                    name="newSlotStatus"
                    title="Slot Status"
                    value={inputs.newSlotStatus}
                    options={SlotStatusList}
                    isClearable={false}
                    onChangeFunc={handleOnChange}
                    errorMsg={errors.newSlotStatus}
                  />
                </div>
              }
            </div>
          </div>
        </form>
      </div>
      <div className="button-container stick-bottom">
        <Button onClick={backFunction}>BACK</Button>
        {JSON.stringify({}) === JSON.stringify(inputs.timeSlot) ||
        inputs.newSlotStatus === "" ? (
          <Button
            onClick={() => {}}
            className="arrow-button arrow-button-disabled"
          >
            SUBMIT
          </Button>
        ) : loading ? (
          <Button
            onClick={() => {}}
            className="arrow-button arrow-button-disabled"
          >
            <span className="arrow">{">"}</span>SUBMITTING
            <span className="arrow">{"<"}</span>
          </Button>
        ) : (
          <Button onClick={handleSubmit} className="arrow-button">
            <span className="arrow">{">"}</span>SUBMIT
            <span className="arrow">{"<"}</span>
          </Button>
        )}
      </div>
    </section>
  );
};
